import type Konva from 'konva';
import { toast } from 'react-toastify';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import { useCanvasStore } from '../store/canvasStore';
import { GifGenerator } from './GifGenerator';

declare module 'konva/lib/Node' {
  interface Node {
    _originalX?: number;
    _originalY?: number;
  }
}

interface Point {
  x: number;
  y: number;
}

interface RGBColor {
  r: number;
  g: number;
  b: number;
}

export const limitDrag = (pos: Point, width: number, height: number): Point => ({
  x: Math.max(-width / 2, Math.min(pos.x, CANVAS_WIDTH - width / 2)),
  y: Math.max(-height / 2, Math.min(pos.y, CANVAS_HEIGHT - height / 2)),
});

export const parseColor = (hex: string): RGBColor => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

export const clampToHex = (val: number): string => {
  return Math.max(0, Math.min(255, Math.round(val)))
    .toString(16)
    .padStart(2, '0');
};

export const exportCanvasImage = (stage: Konva.Stage, format = 'png'): void => {
  if (!stage) return;

  const formatLower = format.toLowerCase();
  const canvasStore = useCanvasStore.getState();

  const originalState = {
    selectedId: canvasStore.selectedId,
    isEditingTextId: canvasStore.isEditingTextId,
  };

  try {
    useCanvasStore.setState({
      selectedId: null,
      isEditingTextId: null,
    });

    setTimeout(() => {
      stage.batchDraw();

      const dataURL = stage.toDataURL({
        mimeType: `image/${formatLower}`,
        quality: formatLower === 'jpeg' ? 0.95 : undefined,
        pixelRatio: 2,
      });

      const link = document.createElement('a');
      const extension = formatLower === 'jpeg' ? 'jpg' : formatLower;
      link.download = `canvas-${new Date().toISOString().slice(0, 10)}.${extension}`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      useCanvasStore.setState({
        selectedId: originalState.selectedId,
        isEditingTextId: originalState.isEditingTextId,
      });
    }, 50);
  } catch (error) {
    toast.error('Failed to export image');

    useCanvasStore.setState({
      selectedId: originalState.selectedId,
      isEditingTextId: originalState.isEditingTextId,
    });
  }
};

interface GifExportOptions {
  duration?: number;
  framesPerSecond?: number;
  quality?: number;
  enhancedAnimation?: boolean;
  dithering?: string;
  progressCallback?: (percent: number) => void;
}

export const exportCanvasGif = async (
  stage: Konva.Stage,
  download = false,
  callback?: (blob: Blob | null) => void,
  options?: GifExportOptions
): Promise<Blob | null> => {
  if (!stage) return null;

  try {
    const state = useCanvasStore.getState();
    const hasEntranceAnimations = [...state.texts, ...state.canvasImages].some((item) =>
      item.animation?.includes('from')
    );

    let fps = 15;
    const duration = options?.duration || 5;

    if (hasEntranceAnimations || duration <= 3) {
      fps = 24;
    } else if (duration > 8) {
      fps = 12;
    }

    const gifGenerator = new GifGenerator(stage, {
      duration: duration,
      framesPerSecond: options?.framesPerSecond || fps,
      quality: options?.quality || 2.0,
    });

    // Store current state
    const currentState = {
      previewMode: state.previewMode,
      selectedId: state.selectedId,
      isEditingTextId: state.isEditingTextId,
    };

    try {
      // Prepare for export
      useCanvasStore.setState({
        selectedId: null,
        isEditingTextId: null,
        previewMode: false,
      });

      await new Promise((resolve) => setTimeout(resolve, 50));

      const blob = await gifGenerator.generateGif(download);

      if (callback) {
        callback(blob);
      }

      return blob;
    } finally {
      // Always restore state
      useCanvasStore.setState({
        previewMode: currentState.previewMode,
        selectedId: currentState.selectedId,
        isEditingTextId: currentState.isEditingTextId,
      });
    }
  } catch (error) {
    if (callback) {
      callback(null);
    }
    return null;
  }
};
